import welcomeSymbol from "@/assets/blackLogo.svg";
import { getBuildingId, WELCOME_GUEST_FOOD_URL } from "@/lib";
import { useSnackbarStore } from "@/store/zustandStore";
import styles from "@/styles/GuestLunchQr.module.scss";
import { NotificationType } from "@/types";
import { Button, PopupCard } from "@app-components";
import type { TFunction } from "i18next";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";

const POSTER = "FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_POSTER";
const POSTER_STEPS = ["STEP_1", "STEP_2", "STEP_3", "STEP_4"] as const;

/** Storage paths are not browser-loadable; only resolved/http(s)/blob/data URLs are. */
const isDisplayableImageUrl = (url?: string) =>
  !!url && /^(https?:|blob:|data:)/i.test(url);

// Poster styles come along from the copied stylesheet; this only sets the page.
// Zero page margin also stops browsers printing their own title/URL header.
const PAGE_STYLE = `
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff !important; }
`;

/** Renders `<b>` in the translation as bold */
const rich = (t: TFunction, key: string) => (
  <Trans t={t} i18nKey={key} components={{ b: <b /> }} />
);

type GuestLunchQrProps = {
  supplierName?: string;
  /** Must be a browser-loadable URL (https/blob/data), not a storage path. */
  supplierLogoUrl?: string;
};

export const GuestLunchQr = ({
  supplierName,
  supplierLogoUrl,
}: GuestLunchQrProps) => {
  const { t, i18n } = useTranslation();
  const { addNotification } = useSnackbarStore();
  const [open, setOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const posterRef = useRef<HTMLDivElement>(null);
  const buildingId = getBuildingId();
  const url = WELCOME_GUEST_FOOD_URL(buildingId);
  const showSupplierLogo =
    !logoFailed && isDisplayableImageUrl(supplierLogoUrl);

  useEffect(() => {
    setLogoFailed(false);
  }, [supplierLogoUrl]);

  const handlePrint = useReactToPrint({
    contentRef: posterRef,
    // Default file name when the admin saves the print as PDF
    documentTitle: `guest-lunch-poster-${buildingId}`,
    pageStyle: PAGE_STYLE,
  });

  if (!url) return null;

  // Guests are often visitors, so the poster is always Norwegian with English below
  const no = i18n.getFixedT("no");
  const en = i18n.getFixedT("en");

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      addNotification(
        t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_COPIED"),
        NotificationType.SUCCESS,
      );
    } catch (error) {
      // Clipboard can be denied or unavailable; the URL stays on screen to copy by hand
      console.error("Failed to copy guest lunch URL:", error);
    }
  };

  return (
    <>
      <Button icon="qr_code" onClick={() => setOpen(true)}>
        {t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_HEADER")}
      </Button>
      <PopupCard
        open={open}
        onClose={() => setOpen(false)}
        titleId="guest-lunch-qr-title"
      >
        <div className={styles.modalBody}>
          <h3 id="guest-lunch-qr-title">
            {t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_HEADER")}
          </h3>
          <p>{t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_DESCRIPTION")}</p>

          <div className={styles.preview}>
            <div ref={posterRef} className={styles.poster}>
              <div className={styles.header}>
                <img
                  className={styles.welcomeLogo}
                  src={welcomeSymbol}
                  alt="Welcome Workdays"
                />
                {showSupplierLogo ? (
                  <img
                    className={styles.brandLogo}
                    src={supplierLogoUrl}
                    alt={supplierName || t("FOOD_ADMIN.RESTAURANT")}
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <span className={styles.brandName}>{supplierName}</span>
                )}
              </div>

              <h1 className={styles.headline}>
                {rich(no, `${POSTER}_HEADLINE`)}
              </h1>
              <p className={styles.headlineEn}>
                {rich(en, `${POSTER}_HEADLINE`)}
              </p>

              <div className={styles.body}>
                <div className={styles.qrTile}>
                  {/* Transparent so the tile tint shows through, as on the canteen poster */}
                  <QRCodeCanvas
                    value={url}
                    size={1024}
                    marginSize={0}
                    bgColor="transparent"
                    style={{ width: "80mm", height: "80mm" }}
                    title={t(`${POSTER}_STEP_1`)}
                  />
                </div>
                <div>
                  <ol className={styles.steps}>
                    {POSTER_STEPS.map((step, index) => (
                      <li key={step} className={styles.step}>
                        <span className={styles.stepNumber} aria-hidden>
                          {index + 1}
                        </span>
                        <span>
                          {no(`${POSTER}_${step}`)}
                          <span className={styles.en}>
                            {en(`${POSTER}_${step}`)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                  <p className={styles.note}>
                    {rich(no, `${POSTER}_NOTE`)}
                    <span className={styles.en}>
                      {rich(en, `${POSTER}_NOTE`)}
                    </span>
                  </p>
                </div>
              </div>

              <div className={styles.footer}>
                {no(`${POSTER}_CANT_SCAN`)}
                <span className={styles.en}>{en(`${POSTER}_CANT_SCAN`)}</span>
                <span className={styles.url}>
                  {url.replace(/^https:\/\//, "")}
                </span>
              </div>
            </div>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            {url}
          </a>
          <div className={styles.actions}>
            <Button dark icon="content_copy" onClick={copyUrl}>
              {t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_COPY")}
            </Button>
            <Button dark icon="print" onClick={handlePrint}>
              {t("FOOD_ADMIN.SETTINGS.GUEST_LUNCH_QR_PRINT")}
            </Button>
          </div>
        </div>
      </PopupCard>
    </>
  );
};
